<script>
import appointmentMixin from '@/components/mixins/appointmentMixin';
import carrierFavoritesMixin from '@/components/mixins/carrierFavoritesMixin';
import recurringAppointmentMixin from '@/components/mixins/recurringAppointmentMixin';
import customFormsMixin from '@satellite/components/mixins/customFormsMixin';
import customFormsService from '@satellite/services/custom-forms-service.js';
import { CustomFieldType } from '@nova/core';

/**
 * @displayName Appointment Details Base
 */
export default {
  mixins: [appointmentMixin, recurringAppointmentMixin, carrierFavoritesMixin, customFormsMixin],
  props: {
    /**
     * Appointment to display details for
     */
    appointment: {
      type: Object,
      required: true
    },
    /**
     * Display status editing options
     */
    shouldEditStatus: {
      type: Boolean,
      required: false,
      default: false
    },
    shouldDisplayCancelButton: {
      type: Boolean,
      required: false,
      default: true
    },
    appointmentWarehouse: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      required: false
    }
  },
  computed: {
    canReschedule() {
      return (
        this.$rolePermissions.canUpdateAppointment &&
        this.novaCore.isRescheduleAllowed(this.appointment.status) &&
        !this.readOnly
      );
    },
    reschedulesCount() {
      return this.novaCore.getReschedulesCount(this.appointment);
    },
    reschedulesString() {
      return this.novaCore.getReschedulesString(this.reschedulesCount);
    },
    gateManagementTriggers() {
      return (
        this.warehouseTriggers?.filter(
          t =>
            this.novaCore.CustomFormsGateManagementFeatures.includes(t.feature) &&
            t.dataEntityName === this.novaCore.CustomFormDataEntitiesEnum.appointment
        ) || []
      );
    },
    claimSupportTriggers() {
      return (
        this.warehouseTriggers?.filter(t =>
          this.novaCore.CustomFormsClaimFeatures.includes(t.feature)
        ) || []
      );
    }
  },
  data() {
    return {
      CustomFieldType,
      showRescheduleDialog: false,
      loading: true,
      tags: this.appointment.tags,
      lockTagEditing: false,
      warehouseTriggers: [],
      loadTypeTriggers: [],
      assetVisitFormData: [],
      appointmentTableRows: {
        dockName: {
          label: 'Dock Name',
          value: `${this.appointment.readableDockName ?? this.appointment.dock?.name}${
            this.appointment.dock.isActive === false ? ' (Inactive)' : ''
          }`,
          tdClass: 'pb-4',
          isHidden: false
        },
        loadType: {
          label: 'Load Type',
          value: '',
          tdClass: 'pb-4',
          isHidden: false
        },
        refNumber: {
          label: 'Reference Number',
          value: this.appointment.refNumber || '----',
          tdClass: 'pb-4',
          isHidden: false
        }
      }
    };
  },
  methods: {
    featureData(feature) {
      return this.novaCore.CustomFormsFeaturesData[feature] || {};
    },
    trackApptDetailsMixpanelEvent() {
      const entryPoint = this.mixpanel.getEntryPoint(this, [
        {
          entryPoint: 'Mobile List View',
          component: 'appointments-list-mobile'
        },
        {
          entryPoint: 'Appointments List',
          component: 'appointments-list'
        },
        {
          entryPoint: 'Self Check-in List',
          component: 'check-in-notification-panel'
        },
        {
          entryPoint: 'Appointments Grid',
          component: 'appointments-page'
        },
        {
          entryPoint: 'Search Appointments',
          component: 'appointments-search-results'
        }
      ]);
      this.mixpanel.track(this.mixpanel.events.MODULE.APPOINTMENT.APPOINTMENT_DETAILS, {
        'Org Name': this.$org.name,
        'Org ID': this.$org.id,
        'Warehouse Name': this.appointmentWarehouse.name,
        'Warehouse ID': this.appointmentWarehouse.id,
        'Appointment ID': this.appointment.id,
        'Entry Point': entryPoint
      });
    },
    initializeRowValues() {
      const refNumSettings = this.$refNumSettings(this.appointmentWarehouse);
      this.appointmentTableRows.loadType.value = this.$loadTypeName(this.appointment.loadType);
      this.appointmentTableRows.refNumber.label = refNumSettings.displayName;
      this.appointmentTableRows.refNumber.isHidden = !refNumSettings.isVisible;
      this.appointmentTableRows.refNumber.value = this.appointment.refNumber;
      this.appointmentTableRows.dockName.value = this.appointment.dock.name;
    },
    async updateAppointmentTags() {
      this.lockTagEditing = true;
      await axios
        .patch(`appointment/${this.appointment.id}`, {
          tags: this.tags
        })
        .then(response => {
          if (response?.data) {
            this.$store.dispatch('Appointments/trackMixpanelEvent', {
              appointment: this.appointment || {},
              change: 'Tags'
            });
            this.$store.dispatch('Appointments/triggerSocketEvent', {
              response,
              appointment: this.appointment
            });
          }
        })
        .finally(() => (this.lockTagEditing = false));
    },
    async setWarehouseTriggers() {
      this.warehouseTriggers = await customFormsService.getWarehouseTriggers(
        this.appointmentWarehouse.id
      );
    },
    async setLoadTypeTriggers() {
      try {
        const response = await customFormsService.getLoadTypeTriggers(this.appointment.loadTypeId);

        this.loadTypeTriggers = response.filter(t => t.flow.formFrom?.formFields.length > 0);
      } catch (error) {
        console.error('Failed to get load type triggers', error);
      }
    },
    async getAssetVisitFormData() {
      if (this.appointment.assetVisit) {
        try {
          return customFormsService.getCustomFormData({
            s: { objectId: this.appointment.assetVisit.id },
            limit: 1000
          });
        } catch (_) {
          return [];
        }
      }
    }
  },
  async fetchTriggers() {
    await this.setWarehouseTriggers();
    await this.setLoadTypeTriggers();
  },
  async mounted() {
    this.initializeRowValues();
    this.setCustomFieldsRows();
    if (this.$selectedWarehouseTriggers) {
      this.warehouseTriggers = this.$selectedWarehouseTriggers;
    } else {
      await this.setWarehouseTriggers();
    }
    await this.setLoadTypeTriggers();
    this.assetVisitFormData = this.getAssetVisitFormData();
    this.loading = false;
    this.$eventHub.$on(`update-Trigger`, this.fetchTriggers);
    this.$eventHub.$on(`create-FormField`, this.fetchTriggers);
  },
  beforeUnmount() {
    this.$router.push({ query: {} });
    Object.keys(this.sockets.actions).map(action => {
      this.$eventHub.$off(`${action}-Trigger`, this.fetchTriggers);
      this.$eventHub.$off(`${action}-FormField`, this.fetchTriggers);
    });
  },
  watch: {
    tags() {
      /**
       * TODO: this watcher is not being triggered when the tags are updated
       * from the Yard view.
       * That's why we are hacking it away and updating the tags manually, in
       * YardViewYard, `handleAppointmentUpdated`.
       */
      if (JSON.stringify(this.tags) !== JSON.stringify(this.appointment.tags)) {
        this.updateAppointmentTags();
      }
    },
    appointment(appointment) {
      this.initializeRowValues();
      this.setCustomFieldsRows();

      /**
       * // TODO: remove, review, or refactor in OD-1694
       * Whatever was intended to happen here is not happening (for
       * Composition API components), so we need to do it manually.
       */
      this.tags = appointment.tags;
    }
  }
};
</script>

<style lang="scss">
.appointment-notes > p:not(:only-child) {
  margin-bottom: 1px !important;
}
</style>
